/**
 * Decode the textual payload returned by a QR reader for an NBS IPS
 * payee/bill QR code (K:PR).
 *
 * Reference: NBS IPS QR (pipe-delimited tag:value payload).
 */

export const NbsIpsErrorCode = Object.freeze({
    EMPTY: 'empty',
    FORMAT: 'format',
    ACCOUNT: 'account',
    PAYEE_NAME: 'payee_name',
    AMOUNT: 'amount',
    PAYMENT_CODE: 'payment_code'
} as const)

export type NbsIpsErrorCode =
    (typeof NbsIpsErrorCode)[keyof typeof NbsIpsErrorCode]

export type NbsIpsFields = {
    payee_account: string
    payee_name: string
    payee_address: string
    amount: string
    payment_code: string
    payer_name?: string
    payer_address?: string
    purpose?: string
    credit_model?: string
    credit_reference?: string
}

export type NbsIpsDecodeResult =
    | { ok: true; fields: NbsIpsFields }
    | { ok: false; errorCode: NbsIpsErrorCode }

function failure(errorCode: NbsIpsErrorCode): NbsIpsDecodeResult {
    return { ok: false, errorCode }
}

function partyFields<Prefix extends 'payee' | 'payer'>(
    value: string,
    prefix: Prefix
): { [K in `${Prefix}_name` | `${Prefix}_address`]: string } {
    const [name, ...address] = String(value)
        .replace(/\r\n?/g, '\n')
        .split('\n')
    return {
        [`${prefix}_name`]: name.trim(),
        [`${prefix}_address`]: address.join(', ').trim()
    } as { [K in `${Prefix}_name` | `${Prefix}_address`]: string }
}

/**
 * Decode an NBS IPS K:PR QR payload.
 */
export function decodeNbsIpsQr(raw: unknown): NbsIpsDecodeResult {
    const text = String(raw || '').trim()
    if (!text) {
        return failure(NbsIpsErrorCode.EMPTY)
    }

    const tags: Record<string, string> = Object.create(null)
    for (const part of text.split('|')) {
        if (part === '') continue

        const separator = part.indexOf(':')
        if (separator === -1) {
            return failure(NbsIpsErrorCode.FORMAT)
        }
        tags[part.slice(0, separator)] = part.slice(separator + 1)
    }

    if (tags.K !== 'PR' || tags.V !== '01' || tags.C !== '1') {
        return failure(NbsIpsErrorCode.FORMAT)
    }

    const account = String(tags.R || '')
    if (!/^\d{18}$/.test(account)) {
        return failure(NbsIpsErrorCode.ACCOUNT)
    }

    const payee = String(tags.N || '')
    if (!payee.trim()) {
        return failure(NbsIpsErrorCode.PAYEE_NAME)
    }

    const amount = /^RSD(\d+)(,(\d{0,2}))?$/.exec(tags.I || '')
    if (!amount) {
        return failure(NbsIpsErrorCode.AMOUNT)
    }

    const paymentCode = String(tags.SF || '')
    if (!/^\d{3}$/.test(paymentCode)) {
        return failure(NbsIpsErrorCode.PAYMENT_CODE)
    }

    const fields: NbsIpsFields = {
        payee_account: account,
        ...partyFields(payee, 'payee'),
        amount: amount[1] + ',' + (amount[3] || '00').padEnd(2, '0'),
        payment_code: paymentCode
    }

    if (tags.P) {
        Object.assign(fields, partyFields(tags.P, 'payer'))
    }
    if (tags.S) {
        fields.purpose = tags.S.trim()
    }
    if (tags.RO && tags.RO.length >= 2) {
        const model = tags.RO.slice(0, 2)
        fields.credit_model = model === '00' ? '0' : model
        fields.credit_reference = tags.RO.slice(2)
    }

    return { ok: true, fields }
}

/** Format 18-digit NBS account as XXX-XXXXXXXXXXXXX-XX. */
export function formatNbsAccount(account: string): string {
    if (!/^\d{18}$/.test(account)) {
        return account
    }
    return `${account.slice(0, 3)}-${account.slice(3, 16)}-${account.slice(16)}`
}

/** Parse Serbian decimal amount string ("1234,56") to a number. */
export function parseNbsAmount(amount: string): number {
    const normalized = amount.replace(',', '.')
    const value = Number.parseFloat(normalized)
    return Number.isFinite(value) ? value : 0
}

/** Format credit model + reference for display. */
export function formatNbsReference(
    model?: string,
    reference?: string
): string {
    if (!model && !reference) {
        return ''
    }
    if (!reference) {
        return model || ''
    }
    if (!model || model === '0') {
        return reference
    }
    return `${model} ${reference}`
}
